import React, { FC } from "react";

import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";

import { PlusIcon } from "@/assets/icons";
import { ProfileFormPanel } from "@/components/organisms";
import { Permissions, Relation } from "@/shared/domain";
import { isABroker, useAuth, useNavigation } from "@/shared/hooks";
import { getFullName } from "@/shared/utils";
import { StyledListEmpty } from "@/styles/styled";
import { validateAddress as addressFormValidation } from "@/validations";

import { RelationsTable } from "../../../components";
import {
    AdministrationFormType,
    ChangeAddressFormType,
    CommercialFormType,
    ContactFormType,
    DriverLicenseAndIdFormType,
    FinanceCompanyFormType,
    FinanceFormType,
    ProfessionFormType,
} from "../../formType";
import {
    addressSchema,
    administrationSchema,
    commercialSchema,
    contactSchema,
    driverLicenseAndIdSchema,
    financeCompanySchema,
    financeSchema,
    PersonalDetailsAreas,
    professionSchema,
} from "../../utils";
import {
    contactFormValidation,
    driverLicenseAndIdFormValidation,
    financeCompanyFormValidation,
    financeFormValidation,
} from "../../validations";
import { StyledTitle } from "./styled";

type PolicyholderContentPanelsProps = {
    personalDetails: MyBrokerApi.PolicyHolderPersonalDetails;
    onSubmitPersonalDetails: (
        formValue:
            | DriverLicenseAndIdFormType
            | FinanceFormType
            | CommercialFormType
            | ProfessionFormType
            | AdministrationFormType
            | FinanceCompanyFormType
            | ContactFormType,
        detailArea: PersonalDetailsAreas,
    ) => void;
    addressDetails: MyBrokerApi.AddressChangeRequest;
    onSubmitAddressDetails: (formValue: ChangeAddressFormType) => void;
    relations: MyBrokerApi.FamilyMembers;
    isLoadingSubmit: boolean;
};

const PolicyholderContentPanels: FC<PolicyholderContentPanelsProps> = ({
    personalDetails,
    onSubmitPersonalDetails,
    isLoadingSubmit,
    addressDetails,
    onSubmitAddressDetails,
    relations,
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { navToRelationDetails, navToAddFamilyMember } = useNavigation();

    const { currentDetails, pendingDetails, isPhysicalPerson = true, policyHolderId } = personalDetails || {};

    const allowAddressChange = user.hasPermission(Permissions.AccessChangeAddress, policyHolderId);
    const allowAddFamilyMember = user.hasPermission(Permissions.AccessAddFamilyMember, policyHolderId);
    const allowEditInformationDetails = user.hasPermission(Permissions.AccessEditInformationDetails, policyHolderId);
    const allowedToViewSalesRepresentative = user.hasPermission(Permissions.ViewSalesRepresentative, policyHolderId);
    const allowedToViewManager = user.hasPermission(Permissions.ViewManager, policyHolderId);

    const { currentAddress, pendingAddress } = addressDetails || {};

    const { currentRelations = [], pendingRelations = [] } = relations || {};
    const allRelations = [...currentRelations, ...pendingRelations];

    const handleRelationClick = (relation: Relation) => {
        const query = {
            policyHolderId: `${policyHolderId}`,
            relationInternalId: `${relation.internalId}`,
            internalpolicyholderId: `${personalDetails?.internalId}`,
            typePartyId: relation.typePartyId,
        };
        return navToRelationDetails(relation.relationDetailsId, query);
    };

    return (
        <>
            <ProfileFormPanel
                dataTestId="contact_form"
                title={t("Common.Contact")}
                panelSchema={contactSchema}
                currentDetails={currentDetails}
                pendingDetails={pendingDetails}
                validationSchema={contactFormValidation()}
                isEditable={allowEditInformationDetails}
                isLoading={isLoadingSubmit}
                onSubmit={(formValue) => {
                    onSubmitPersonalDetails(formValue, PersonalDetailsAreas.CONTACT);
                }}
            />
            {allowAddressChange && (
                <ProfileFormPanel
                    dataTestId="address_form"
                    title={t("Common.Address")}
                    stringDivisor=", "
                    panelSchema={addressSchema(currentRelations)}
                    currentDetails={currentAddress?.addressDetail}
                    pendingDetails={pendingAddress?.addressDetail}
                    validationSchema={addressFormValidation()}
                    isEditable={allowEditInformationDetails}
                    isLoading={isLoadingSubmit}
                    onSubmit={onSubmitAddressDetails}
                />
            )}
            {isPhysicalPerson ? (
                <>
                    <ProfileFormPanel
                        dataTestId="drive_license_form"
                        title={t("User.PersonalDetails.DriverLicenseAndId.Title")}
                        panelSchema={driverLicenseAndIdSchema}
                        currentDetails={currentDetails}
                        pendingDetails={pendingDetails}
                        isEditable={allowEditInformationDetails}
                        validationSchema={driverLicenseAndIdFormValidation(
                            pendingDetails?.birthdate || currentDetails?.birthdate,
                        )}
                        isLoading={isLoadingSubmit}
                        onSubmit={(formValue) => {
                            onSubmitPersonalDetails(formValue, PersonalDetailsAreas.IDENTITY_AND_DRIVER_LICENSE);
                        }}
                    />
                    <ProfileFormPanel
                        dataTestId="finance_form"
                        title={t("User.PersonalDetails.Finance.Title")}
                        panelSchema={financeSchema}
                        currentDetails={currentDetails}
                        pendingDetails={pendingDetails}
                        isEditable={allowEditInformationDetails}
                        validationSchema={financeFormValidation()}
                        isLoading={isLoadingSubmit}
                        onSubmit={(formValue) => {
                            onSubmitPersonalDetails(formValue, PersonalDetailsAreas.FINANCE);
                        }}
                    />
                    <ProfileFormPanel
                        dataTestId="profession_form"
                        title={t("User.PersonalDetails.Profession.Title")}
                        panelSchema={professionSchema}
                        currentDetails={currentDetails}
                        pendingDetails={pendingDetails}
                        isEditable={allowEditInformationDetails}
                        isLoading={isLoadingSubmit}
                        onSubmit={(formValue) => {
                            onSubmitPersonalDetails(formValue, PersonalDetailsAreas.PROFESSION);
                        }}
                    />
                </>
            ) : (
                <ProfileFormPanel
                    dataTestId="finance_form"
                    title={t("User.PersonalDetails.Finance.TitleEnterprise")}
                    panelSchema={financeCompanySchema}
                    currentDetails={currentDetails}
                    pendingDetails={pendingDetails}
                    isEditable={allowEditInformationDetails}
                    validationSchema={financeCompanyFormValidation()}
                    isLoading={isLoadingSubmit}
                    onSubmit={(formValue) => {
                        onSubmitPersonalDetails(formValue, PersonalDetailsAreas.FINANCE);
                    }}
                />
            )}

            {allowedToViewSalesRepresentative && currentDetails.commercialDelegate && (
                <ProfileFormPanel
                    dataTestId="commercial_delegate_form"
                    title={t("User.PersonalDetails.CommercialDelegate.Title")}
                    panelSchema={commercialSchema}
                    currentDetails={{ ...personalDetails, ...currentDetails }}
                    pendingDetails={pendingDetails}
                />
            )}
            {allowedToViewManager &&
                (currentDetails.administrator?.name ||
                    currentDetails.administrator?.email ||
                    currentDetails.administrator?.phone) && (
                    <ProfileFormPanel
                        dataTestId="administration_form"
                        title={t("Common.Manager")}
                        panelSchema={administrationSchema}
                        currentDetails={currentDetails}
                        pendingDetails={pendingDetails}
                    />
                )}
            {isPhysicalPerson && (
                <>
                    <StyledTitle>{t("User.PersonalDetails.Relationships.Title")}</StyledTitle>
                    {allRelations.length > 0 ? (
                        <RelationsTable relations={allRelations} onRelationClick={handleRelationClick} />
                    ) : (
                        <StyledListEmpty data-testid="empty-relations">
                            {t("User.PersonalDetails.Relationships.Empty")}
                        </StyledListEmpty>
                    )}

                    {allowAddFamilyMember && (
                        <Button
                            data-testid="user_personal_details_page-button-addFamilyMember"
                            className="float-right mt10 mb20"
                            disabled={isABroker()}
                            title={`${isABroker() ? t("Common.PreviewUserNotAllowed") : ""}`}
                            onClick={() =>
                                navToAddFamilyMember(
                                    policyHolderId,
                                    getFullName(currentDetails?.lastName, currentDetails?.firstName),
                                )
                            }
                        >
                            <PlusIcon className="mr10" /> {t("User.PersonalDetails.Relationships.AddNewMember")}
                        </Button>
                    )}
                </>
            )}
        </>
    );
};

export default PolicyholderContentPanels;
